/**
 * Reaction rules and the bounded active-burst list. Pure: no DOM, no timers.
 *
 * Reactions are ephemeral: the server broadcasts an event, every client shows
 * it for a short while, and nothing is stored or replayed on join. Two bounds
 * from plan/04 item 2/3 live here:
 *
 * - **Active animations are capped.** A flood must not turn into hundreds of
 *   concurrently animating nodes competing with video decoding, so the oldest
 *   bursts are dropped once the cap is reached. Dropping the oldest (rather
 *   than refusing the newest) keeps the display current under load.
 * - **Sends are spaced.** The server enforces a per-identity minimum interval
 *   and rejects anything faster; mirroring it locally turns a double click
 *   into a briefly disabled button instead of a `rate-limited` rejection.
 *
 * The reaction set is a fixed, curated list — the wire type is a closed enum,
 * so a client can neither invent a kind nor inject an asset or stylesheet.
 */

import type { ReactionKind } from '../generated';
import type { ReactionEvent } from '../room/protocol';

/**
 * How long one burst stays on screen (plan/04 item 2 default: 3 seconds).
 * Reduced-motion users see it appear statically for the same period; the
 * meaning is never suppressed, only the movement.
 */
export const REACTION_LIFETIME_MS = 3000;

/** Simultaneously animating bursts. Beyond this the oldest are dropped. */
export const REACTION_MAX_ACTIVE = 8;

/**
 * Server minimum interval between one identity's reactions
 * (`--reaction-min-interval-ms`, default 400). Sending faster is rejected as
 * `rate-limited`, so the button stays disabled for this long after a click.
 */
export const REACTION_SEND_INTERVAL_MS = 400;

/** Horizontal lanes bursts are spread across so a flurry does not overlap. */
export const REACTION_LANES = 4;

export interface ReactionChoice {
  readonly kind: ReactionKind;
  /** Presentational glyph; the label is what assistive tech announces. */
  readonly glyph: string;
  readonly label: string;
}

/** The curated set, keyed for lookup when a broadcast arrives. */
const CHOICE_BY_KIND: Record<ReactionKind, ReactionChoice> = {
  'thumbs-up': { kind: 'thumbs-up', glyph: '👍', label: 'Thumbs up' },
  laugh: { kind: 'laugh', glyph: '😂', label: 'Laugh' },
  heart: { kind: 'heart', glyph: '❤️', label: 'Heart' },
  surprised: { kind: 'surprised', glyph: '😮', label: 'Surprised' },
  clap: { kind: 'clap', glyph: '👏', label: 'Clap' },
};

/** Button order, which is the declaration order above. */
export const REACTION_CHOICES: readonly ReactionChoice[] = Object.values(CHOICE_BY_KIND);

/** One reaction currently displayed. */
export interface ReactionBurst {
  /** Server-issued reaction ID; also the duplicate-suppression key. */
  readonly id: string;
  /** Local monotonic sequence, used as the render key and the lane source. */
  readonly seq: number;
  readonly username: string;
  readonly kind: ReactionKind;
  readonly glyph: string;
  readonly label: string;
  /** Client monotonic time the burst became visible. */
  readonly startedAtMs: number;
  /** `0..REACTION_LANES-1` horizontal slot. */
  readonly lane: number;
}

/** Drops bursts whose lifetime has elapsed; same reference when none did. */
export function pruneReactionBursts(
  bursts: readonly ReactionBurst[],
  nowMs: number,
  lifetimeMs: number = REACTION_LIFETIME_MS,
): readonly ReactionBurst[] {
  const live = bursts.filter((burst) => nowMs - burst.startedAtMs < lifetimeMs);
  return live.length === bursts.length ? bursts : live;
}

/**
 * Adds one broadcast reaction to the active list, after pruning expired
 * bursts and dropping the oldest survivors that exceed `maxActive`.
 *
 * A repeated reaction ID (a duplicated frame, or a re-delivery) is ignored:
 * the same reaction must not animate twice.
 */
export function appendReactionBurst(
  bursts: readonly ReactionBurst[],
  event: ReactionEvent,
  nowMs: number,
  seq: number,
  maxActive: number = REACTION_MAX_ACTIVE,
): readonly ReactionBurst[] {
  if (bursts.some((burst) => burst.id === event.id)) return bursts;
  const live = pruneReactionBursts(bursts, nowMs);
  const choice = CHOICE_BY_KIND[event.kind];
  const burst: ReactionBurst = {
    id: event.id,
    seq,
    username: event.username,
    kind: event.kind,
    glyph: choice.glyph,
    label: choice.label,
    startedAtMs: nowMs,
    lane: seq % REACTION_LANES,
  };
  const next = [...live, burst];
  return next.length > maxActive ? next.slice(next.length - maxActive) : next;
}

/**
 * Milliseconds left before this client may send another reaction; `0` when it
 * may send now. Used both to disable the buttons and to schedule the re-enable,
 * so the two cannot drift apart.
 */
export function reactionCooldownMs(
  lastSentAtMs: number | null,
  nowMs: number,
  intervalMs: number = REACTION_SEND_INTERVAL_MS,
): number {
  if (lastSentAtMs === null) return 0;
  return Math.max(0, Math.ceil(lastSentAtMs + intervalMs - nowMs));
}
